// Ofisiantın opsiya seçimi — ölçü (Kiçik/Orta/Böyük) və əlavə tərkiblər.
//
// NƏ ÜÇÜN AYRI MODAL:
// Məhsulun opsiya qrupu yoxdursa bu modal ÜMUMİYYƏTLƏ açılmır — klikləmək
// məhsulu birbaşa səbətə salır, yəni köhnə axın toxunulmaz qalır. Bu, istifadəçinin
// tələbidir: heç bir restoran/məhsul opsiya əlavə etməyə məcbur deyil.
//
// QAYDA:
//   minSelect > 0   → məcburi; düymə seçim tamamlanana qədər bağlıdır
//   maxSelect === 1 → radio davranışı (yeni seçim köhnəni əvəz edir)
//   maxSelect null  → limitsiz
// Eyni yoxlama pos_local-da da var (`checkModifierRules`) — bu ekran yalnız
// rahatlıq üçündür, təhlükəsizlik sərhədi deyil.

import { useEffect, useMemo, useState } from "react";
import { X, Check, Minus, Plus } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";

export interface OptionChoice {
  id: string;
  name: string;
  priceDelta: number;
  isDefault?: boolean;
}

export interface OptionGroup {
  id: string;
  name: string;
  minSelect: number;
  maxSelect: number | null;
  options: OptionChoice[];
}

export interface OptionSelection {
  /** pos_local-a göndəriləcək id-lər (sıra dəyişməz saxlanılır). */
  modifierIds: string[];
  /** Səbətdə göstərmək üçün seçilmiş adlar. */
  labels: string[];
  /** Bir ədədə düşən ümumi qiymət fərqi (mənfi ola bilər). */
  priceDelta: number;
  quantity: number;
}

interface Props {
  productName: string;
  basePrice: number;
  groups: OptionGroup[];
  onCancel: () => void;
  onConfirm: (selection: OptionSelection) => void;
}

const money = (n: number) => `₼${n.toFixed(2)}`;

export function ProductOptionsModal({
  productName,
  basePrice,
  groups,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [quantity, setQuantity] = useState(1);

  // Standart variantlar əvvəlcədən işarələnir (məs. "Orta" ölçü).
  // maxSelect limitini standartlar da aşa bilməz.
  useEffect(() => {
    const initial: Record<string, string[]> = {};
    for (const g of groups) {
      const defaults = g.options.filter((o) => o.isDefault).map((o) => o.id);
      const limit = g.maxSelect === null ? defaults.length : g.maxSelect;
      initial[g.id] = defaults.slice(0, limit);
    }
    setSelected(initial);
  }, [groups]);

  const toggle = (group: OptionGroup, optionId: string) => {
    setSelected((prev) => {
      const current = prev[group.id] ?? [];
      const isOn = current.includes(optionId);

      // Tək seçimli qrup radio kimi işləyir. Məcburi deyilsə eyni variantı
      // yenidən klikləmək seçimi ləğv edir; məcburidirsə boş qala bilməz.
      if (group.maxSelect === 1) {
        if (isOn) return group.minSelect > 0 ? prev : { ...prev, [group.id]: [] };
        return { ...prev, [group.id]: [optionId] };
      }

      if (isOn) {
        return { ...prev, [group.id]: current.filter((id) => id !== optionId) };
      }
      if (group.maxSelect !== null && current.length >= group.maxSelect) {
        return prev; // limit dolub — səssizcə saxla, düymə onsuz da sönükdür
      }
      return { ...prev, [group.id]: [...current, optionId] };
    });
  };

  const { unmetGroup, priceDelta, chosen } = useMemo(() => {
    let delta = 0;
    const labels: string[] = [];
    const ids: string[] = [];
    let unmet: OptionGroup | null = null;

    for (const g of groups) {
      const picks = selected[g.id] ?? [];
      if (picks.length < g.minSelect && !unmet) unmet = g;
      for (const optId of picks) {
        const opt = g.options.find((o) => o.id === optId);
        if (!opt) continue;
        delta += opt.priceDelta;
        labels.push(opt.name);
        ids.push(opt.id);
      }
    }
    return { unmetGroup: unmet, priceDelta: delta, chosen: { ids, labels } };
  }, [groups, selected]);

  const unitPrice = basePrice + priceDelta;

  const confirm = () => {
    if (unmetGroup) return;
    onConfirm({
      modifierIds: chosen.ids,
      labels: chosen.labels,
      priceDelta,
      quantity,
    });
  };

  const ruleLabel = (g: OptionGroup) => {
    if (g.minSelect > 0 && g.maxSelect === 1) return t("opt.required");
    if (g.minSelect > 0) return t("opt.minN").replace("{n}", String(g.minSelect));
    if (g.maxSelect !== null) return t("opt.maxN").replace("{n}", String(g.maxSelect));
    return t("opt.optional");
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white dark:bg-[#111318] w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Başlıq */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-white/[0.06]">
          <div className="min-w-0">
            <h3 className="font-black text-lg text-gray-900 dark:text-white truncate">
              {productName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {money(basePrice)}
            </p>
          </div>
          <button
            onClick={onCancel}
            aria-label={t("common.close")}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Qruplar */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {groups.map((g) => {
            const picks = selected[g.id] ?? [];
            const full =
              g.maxSelect !== null && g.maxSelect > 1 && picks.length >= g.maxSelect;
            return (
              <div key={g.id}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-sm uppercase tracking-wide text-gray-900 dark:text-white">
                    {g.name}
                  </h4>
                  <span
                    className={`text-[10px] px-2 py-1 rounded-full font-bold ${
                      g.minSelect > 0
                        ? "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300"
                        : "bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400"
                    }`}
                  >
                    {ruleLabel(g)}
                  </span>
                </div>

                <div className="space-y-2">
                  {g.options.map((o) => {
                    const on = picks.includes(o.id);
                    // Limit dolub və bu variant seçili deyilsə → əlçatmaz.
                    const blocked = !on && full;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        disabled={blocked}
                        onClick={() => toggle(g, o.id)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                          on
                            ? "border-[var(--primary)] bg-orange-50 dark:bg-orange-500/10"
                            : "border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20"
                        } ${blocked ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <span
                            className={`w-5 h-5 shrink-0 flex items-center justify-center border-2 ${
                              g.maxSelect === 1 ? "rounded-full" : "rounded-md"
                            } ${
                              on
                                ? "border-[var(--primary)] bg-[var(--primary)]"
                                : "border-gray-300 dark:border-white/20"
                            }`}
                          >
                            {on && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                            {o.name}
                          </span>
                        </span>
                        {/* Fərq 0 olanda qiymət yazılmır — "+₼0" səs-küydür. */}
                        {o.priceDelta !== 0 && (
                          <span
                            className={`text-sm font-bold shrink-0 ${
                              o.priceDelta < 0
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-gray-600 dark:text-gray-300"
                            }`}
                          >
                            {o.priceDelta < 0 ? "−" : "+"}
                            {money(Math.abs(o.priceDelta))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Alt panel */}
        <div className="p-5 border-t border-gray-100 dark:border-white/[0.06] space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 bg-gray-100 dark:bg-white/[0.06] rounded-xl p-1">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="p-2 rounded-lg hover:bg-white dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
                aria-label="-"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-black w-6 text-center text-gray-900 dark:text-white">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="p-2 rounded-lg hover:bg-white dark:hover:bg-white/10 text-gray-600 dark:text-gray-300"
                aria-label="+"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <span className="text-xl font-black text-gray-900 dark:text-white">
              {money(unitPrice * quantity)}
            </span>
          </div>

          <button
            onClick={confirm}
            disabled={!!unmetGroup}
            className="w-full py-4 rounded-xl font-bold text-white bg-[var(--primary)] hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {unmetGroup
              ? t("opt.selectFirst").replace("{group}", unmetGroup.name)
              : t("opt.addToCart")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductOptionsModal;
